import { InfoOutlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { GridColDef, GridInitialState, GridRowParams } from "@mui/x-data-grid";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import ResultsPage, {
  ResultsPageProps,
} from "../../components/common/ResultsPage/ResultsPage";
import { useAlert } from "../../context/AlertContext";
import { IAuditLog } from "../../types/audit-logs/auditLog";
import DictionaryGridView from "../../components/common/DataTable/DictionaryGridView";
import { RowSelectorMode } from "../../types/datatable/RowSelectorMode";
import { useTranslation } from "react-i18next";

export interface AuditLogResultsViewProps
  extends Omit<
    ResultsPageProps<IAuditLog>,
    | "columns"
    | "permissionResource"
    | "tableId"
    | "entityName"
    | "selectedRowId"
    | "setSelectedRowId"
  > {
  selectedAuditLogId: string | null;
  setSelectedAuditLogId: (id: string | null) => void;
  setIsViewModalOpen: (isOpen: boolean) => void;
  rows: IAuditLog[];
  error?: Error | null;
  isLoading?: boolean;
}

const isGuid = (guid: string | undefined) => {
  if (!guid) return false;
  try {
    const guidSchema = z.string().uuid();
    guidSchema.parse(guid);
    return true;
  } catch {
    return false;
  }
};

const findEndpoint = (path: string | undefined) => {
  if (!path) return;
  const trimmedPath = path.replace(
    /(?<leadingSlash>^\/)|(?<trailingSlash>\/$)/g,
    ""
  );
  return trimmedPath
    .split("/")
    .filter((x) => !isGuid(x))
    .pop();
};

const AuditLogResultsView = ({
  selectedAuditLogId,
  setSelectedAuditLogId,
  setIsViewModalOpen,
  rows,
  error,
  isLoading,
  ...props
}: AuditLogResultsViewProps) => {
  const { t } = useTranslation("grid");
  const showAlert = useAlert();
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "auditLogId",
        headerName: t("grid:id"),
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: "event",
        headerName: t("grid:event"),
        valueGetter: (_: string, row: IAuditLog) => {
          const { httpMethod, path } = row;
          const endpoint = findEndpoint(path);
          if (!httpMethod) return "N/A";
          if (!endpoint) return httpMethod;
          return `${httpMethod} ${endpoint}`;
        },
        hideable: false,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: "payload",
        headerName: t("grid:changes"),
        renderCell: (params) => <DictionaryGridView {...params} />,
      },
      {
        field: "resourceId",
        headerName: t("grid:resourceId"),
      },
      {
        field: "date",
        headerName: t("grid:eventTime"),
        valueFormatter: (value: string) => new Date(value).toLocaleString(),
        hideable: false,
        minWidth: 175,
        maxWidth: 225,
      },
      {
        field: "userName",
        headerName: t("grid:user"),
        hideable: false,
        minWidth: 200,
        maxWidth: 400,
      },
      {
        field: "httpMethod",
        headerName: t("grid:httpMethod"),
        minWidth: 100,
        maxWidth: 200,
      },
      {
        field: "statusCode",
        headerName: t("grid:statusCode"),
        minWidth: 100,
        maxWidth: 125,
      },
      {
        field: "path",
        headerName: t("grid:fullUrlPath"),
        cellClassName: "break-words",
        minWidth: 150,
        maxWidth: 500,
      },
      {
        field: "queryString",
        headerName: t("grid:queryString"),
        cellClassName: "break-words",
        minWidth: 150,
      },
      {
        field: "ipAddress",
        headerName: t("grid:ipAddress"),
        minWidth: 115,
        maxWidth: 275,
      },
      {
        field: "View Log",
        headerName: "",
        type: "actions",
        getActions: ({ row }: GridRowParams<IAuditLog>) => {
          return [
            <IconButton
              key={`${row.auditLogId}-view-btn`}
              onClick={() => {
                setSelectedAuditLogId(row.auditLogId);
                setIsViewModalOpen(true);
              }}
            >
              <InfoOutlined />
            </IconButton>,
          ];
        },
        minWidth: 50,
        maxWidth: 100,
      },
    ],
    [setIsViewModalOpen, setSelectedAuditLogId, t]
  );
  const initialState: GridInitialState = {
    columns: {
      columnVisibilityModel: {
        auditLogId: false,
        statusCode: false,
        queryString: false,
        httpMethod: false,
        path: false,
      },
    },
  };

  useEffect(() => {
    if (!error) return;
    showAlert(error.message, "error");
  }, [error, showAlert]);

  return (
    <ResultsPage
      {...props}
      permissionResource="auditlogs"
      tableId="audit-logs"
      entityName="Audit Log"
      paginationMode="server"
      columns={columns}
      rows={rows}
      selectedRowId={selectedAuditLogId}
      setSelectedRowId={setSelectedAuditLogId}
      getRowId={(row: IAuditLog) => row.auditLogId}
      loading={isLoading}
      initialState={initialState}
      disableCreatable
      disableEditable
      disableDeletable
      hideDefaultButtons
      rowSelectorMode={RowSelectorMode.MULTIPLE}
      autoSizeRows={true}
    />
  );
};

export default AuditLogResultsView;
